import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, resolvePhotoUrl } from "@/lib/api";
import { loadGoogleAnalytics, loadMetaPixel } from "@/lib/analytics";

const SettingsCtx = createContext(null);

const FALLBACK = {
  website_name: "PRONET",
  logo_url: "",
  id_card_background_url: "",
  avatar_presets: [
    { key: "portrait-1", label: "Avatar 1", image_url: "" },
    { key: "portrait-2", label: "Avatar 2", image_url: "" },
    { key: "portrait-3", label: "Avatar 3", image_url: "" },
    { key: "portrait-4", label: "Avatar 4", image_url: "" },
  ],
  social_links: [],
  contact_email: "contact@photographersnetwork.in",
  contact_phone: "",
  whatsapp_number: "",
  telegram_link: "",
  hero: {
    badge: "India's creative professional network",
    title_line1: "Find the right", title_accent: "photographer", title_line2: "near you.", title_tail: "",
    description: "Search photographers, videographers and creative specialists by category and location.",
    cta_primary_label: "Find a professional", cta_primary_link: "/gomore",
    cta_secondary_label: "Join PRONET", cta_secondary_link: "/register",
    cta_tertiary_label: "", cta_tertiary_link: "/about",
    image_url: "https://images.unsplash.com/photo-1554048612-b6a482bc67e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    card_overline: "Reviewed profiles",
    card_body: "Real professionals. Clear details.",
  },
  home_features: [],
  home_banners: [],
  categories: [
    { name: "Photography", icon: "Camera", enabled: true },
    { name: "Videography", icon: "Video", enabled: true },
    { name: "Wedding Photography", icon: "Heart", enabled: true },
    { name: "Wedding Videography", icon: "Sparkles", enabled: true },
    { name: "Candid Photography", icon: "Smile", enabled: true },
    { name: "Traditional Photography", icon: "Aperture", enabled: true },
    { name: "Cinematic Videography", icon: "Film", enabled: true },
    { name: "Drone Photography", icon: "Plane", enabled: true },
    { name: "Drone Videography", icon: "Plane", enabled: true },
    { name: "Photo Editing", icon: "Wand2", enabled: true },
    { name: "Video Editing", icon: "Scissors", enabled: true },
    { name: "Album Designing", icon: "BookOpen", enabled: true },
    { name: "Live Streaming", icon: "Radio", enabled: true },
    { name: "Studio Photography", icon: "Lamp", enabled: true },
    { name: "LED", icon: "Lightbulb", enabled: true },
    { name: "Jimmy Jib", icon: "Move", enabled: true },
  ],
  locations: { states: [], districts: [], cities: [] },
  about_page: {},
  contact_page: { show_admin_link: true },
  footer: { copyright: "© KANGRUPHOTO 2019", tagline: "Find photographers, videographers and creative professionals across India." },
  // This is PRONET's public GA4 web-stream ID (not a secret). The backend
  // normally supplies it, while this fallback keeps tracking active during
  // a temporary settings API failure.
  google_analytics_id: "G-2TJTWWF3XH",
  meta_pixel_id: "",
  how_to_register_videos: [],
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const refresh = useCallback(() => {
    api.get("/public/settings")
      .then((r) => setSettings({ ...FALLBACK, ...r.data, hero: { ...FALLBACK.hero, ...(r.data.hero || {}) },
        contact_page: { ...FALLBACK.contact_page, ...(r.data.contact_page || {}) },
        footer: { ...FALLBACK.footer, ...(r.data.footer || {}) } }))
      .catch(() => setSettings(FALLBACK))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Keep the browser tab icon in sync with the site logo uploaded in Admin CMS.
  useEffect(() => {
    const url = resolvePhotoUrl(settings.logo_url);
    if (!url) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  }, [settings.logo_url]);

  useEffect(() => {
    if (settings.google_analytics_id) loadGoogleAnalytics(settings.google_analytics_id);
  }, [settings.google_analytics_id]);

  useEffect(() => {
    if (settings.meta_pixel_id) loadMetaPixel(settings.meta_pixel_id);
  }, [settings.meta_pixel_id]);

  return <SettingsCtx.Provider value={{ settings, loaded, refresh }}>{children}</SettingsCtx.Provider>;
}

export const useSettings = () => useContext(SettingsCtx) || { settings: FALLBACK, loaded: false, refresh: () => {} };
